import {
  Events,
  IStatusPanelComp,
  IStatusPanelParams,
} from '@ag-grid-community/core'
import { loadTemplate } from '@ag-grid-community/core/dist/cjs/utils/dom'
import { prettyFormatNumber } from '@helios/utils'
import { WorkbenchGridContext } from '../../models/grid-context.model'
import { computeRangeSum } from '../cell-range-sum'
import {
  EMPTY_PANEL_CLASS,
  statusPanelTemplate,
} from './workbench-status-bar-base'

const CONTAINER_ID = 'wb-range-sum-container'

/**
 * `Sum:` for the current cell range — select numeric cells and read their total, grouped by
 * currency. Only applies to the sources that opt in via {@link WorkbenchGridContext.rangeSumEnabled}
 * and stays collapsed until a range actually covers numeric cells.
 */
export class WorkbenchRangeSumStatusBarComp implements IStatusPanelComp {
  private readonly eGui = loadTemplate(
    statusPanelTemplate(
      'Sum',
      `<select class="form-control" id="${CONTAINER_ID}"></select>`,
      'hs-workbench-range-sum-status-bar',
    ),
  )

  private params?: IStatusPanelParams

  /** @internal */
  readonly refreshListener = () => this.refresh()

  init(params: IStatusPanelParams): void {
    this.params = params
    params.api.addEventListener(
      Events.EVENT_RANGE_SELECTION_CHANGED,
      this.refreshListener,
    )
    // A source swap replaces the row data and clears any range; re-evaluate so the panel collapses.
    params.api.addEventListener(
      Events.EVENT_MODEL_UPDATED,
      this.refreshListener,
    )
    this.refresh()
  }

  destroy(): void {
    this.params?.api.removeEventListener(
      Events.EVENT_RANGE_SELECTION_CHANGED,
      this.refreshListener,
    )
    this.params?.api.removeEventListener(
      Events.EVENT_MODEL_UPDATED,
      this.refreshListener,
    )
  }

  getGui(): HTMLElement {
    return this.eGui
  }

  /** @internal */
  refresh(): void {
    const api = this.params?.api
    const context: WorkbenchGridContext | undefined = this.params?.context
    if (api == null || context?.rangeSumEnabled !== true) {
      this.setEmptyState(true)
      return
    }

    const { count, byCurrency, columnLabel } = computeRangeSum(api)
    this.setEmptyState(count === 0)
    if (count === 0) return

    this.setLabel(columnLabel ? `Sum · ${columnLabel}:` : 'Sum:')
    this.setOptions(
      Object.entries(byCurrency).map(([currency, total]) => {
        // `computeRangeSum` keys plain (non-Money) numbers under ''.
        const amount = prettyFormatNumber(total, 4)
        return loadTemplate(
          `<option>${currency ? `${amount} ${currency}` : amount}</option>`,
        )
      }),
    )
  }

  private setLabel(text: string): void {
    const label = this.eGui.querySelector('label')
    if (label == null) return
    label.textContent = text
    // This is the only status-bar label that carries a column name, so it is also the only one that
    // can be truncated. Keep the full text reachable on hover.
    label.title = text
  }

  private setOptions(options: HTMLElement[]): void {
    const select = this.eGui.querySelector<HTMLSelectElement>(
      `#${CONTAINER_ID}`,
    )
    if (select == null) return

    const previousIndex = Math.max(0, select.selectedIndex)
    select.innerHTML = ''
    options.forEach(option => select.append(option))
    select.selectedIndex = Math.min(previousIndex, options.length - 1)
  }

  private setEmptyState(isEmpty: boolean): void {
    this.eGui.classList.toggle(EMPTY_PANEL_CLASS, isEmpty)
    const container = this.eGui.getElementsByClassName('value-container')?.[0]
    if (isEmpty) container?.classList.add('empty')
    else container?.classList.remove('empty')
  }
}
